/**
 * The admin console's calls: teams, seats, members, invitations and invite requests.
 *
 * Through the PUBLIC API (`client.ts`), not through PostgREST. `reads.ts` exists for the one
 * sanctioned exception api-platform.md §16 carries — a browser reading an RLS-guarded VIEW to skip
 * a 2.4-second detour through a Worker in Amsterdam — and none of these qualify: they are writes,
 * or they are reads whose narrowing is the ROUTE's (`members.manage`, `canManageWorkspace`) rather
 * than a policy's. A console screen that read `workspaces` directly would see every team of the org
 * and be unable to tell which ones it may administer, because that is not a question RLS answers.
 *
 * Every shape is validated at the boundary by hand, the same way `reads.ts` validates its views and
 * for the same measured reason: zod is ~13 KB gzipped in the chunk the budget charges to every
 * surface, to check shapes with a dozen fields between them.
 */
import { api } from './client'

export type WorkspaceResource = {
  readonly id: string
  readonly name: string
  readonly is_default: boolean
  readonly member_count: number
  readonly my_role: string | null
}

export type WorkspaceMemberResource = {
  readonly id: string
  readonly org_membership_id: string
  /**
   * The ACCOUNT id, which is the one an escalation rung stores (ADR 0161).
   *
   * `id` above is the workspace membership and `org_membership_id` the org one; neither resolves to
   * a person at dispatch. All three are uuids on one row, so picking the wrong one type-checks,
   * saves, and fails weeks later as an undeliverable escalation — which is why the plan CHECK
   * refuses a `mem_…` value and why this field exists rather than being derived.
   */
  readonly user_id: string | null
  readonly role: string
  readonly org_role: string
  readonly status: string
  readonly email: string | null
  readonly display_name: string | null
}

export type OrgMemberResource = {
  readonly id: string
  readonly user_id: string
  readonly role: string
  readonly status: string
  readonly display_name: string | null
  readonly email: string | null
  readonly workspaces: ReadonlyArray<{
    readonly id: string
    readonly name: string
    readonly role: string
  }>
  readonly last_active_at: string | null
  readonly joined_at: string | null
}

export type InvitationResource = {
  readonly id: string
  readonly email: string
  readonly role: string
  readonly workspace_id: string | null
  readonly workspace_role: string | null
  readonly group_id: string | null
  readonly permission_profile_id: string | null
  readonly expires_at: string
  readonly created_at: string
  readonly last_sent_at: string | null
  readonly invitation_url?: string
}

export type InviteRequestResource = {
  readonly id: string
  readonly email: string
  readonly requested_role: string
  readonly workspace_id: string | null
  readonly message: string | null
  readonly status: string
  readonly created_at: string
}

export async function listWorkspaces(): Promise<readonly WorkspaceResource[]> {
  const page = await api<{ readonly data: readonly WorkspaceResource[] }>('/v1/workspaces')
  return page.data
}

export async function createWorkspace(name: string): Promise<WorkspaceResource> {
  const page = await api<{ readonly data: WorkspaceResource }>('/v1/workspaces', {
    method: 'POST',
    body: { name },
  })
  return page.data
}

export async function renameWorkspace(id: string, name: string): Promise<WorkspaceResource> {
  const page = await api<{ readonly data: WorkspaceResource }>(`/v1/workspaces/${id}`, {
    method: 'PATCH',
    body: { name },
  })
  return page.data
}

export type WorkspaceDeletionResource = {
  readonly envelopes_rehomed: number
  readonly templates_rehomed: number
  readonly contacts_rehomed: number
  readonly members_removed: number
}

export async function deleteWorkspace(id: string): Promise<WorkspaceDeletionResource> {
  const page = await api<{ readonly data: WorkspaceDeletionResource }>(`/v1/workspaces/${id}`, {
    method: 'DELETE',
  })
  return page.data
}

export async function listWorkspaceMembers(
  id: string,
): Promise<readonly WorkspaceMemberResource[]> {
  const page = await api<{ readonly data: readonly WorkspaceMemberResource[] }>(
    `/v1/workspaces/${id}/members`,
  )
  return page.data
}

export async function addWorkspaceMember(
  workspaceId: string,
  input: { readonly email?: string; readonly org_membership_id?: string; readonly role?: string },
): Promise<WorkspaceMemberResource> {
  const page = await api<{ readonly data: WorkspaceMemberResource }>(
    `/v1/workspaces/${workspaceId}/members`,
    { method: 'POST', body: input },
  )
  return page.data
}

export async function setWorkspaceMemberRole(
  workspaceId: string,
  memberId: string,
  role: string,
): Promise<void> {
  await api(`/v1/workspaces/${workspaceId}/members/${memberId}`, {
    method: 'PATCH',
    body: { role },
  })
}

export async function removeWorkspaceMember(workspaceId: string, memberId: string): Promise<void> {
  await api(`/v1/workspaces/${workspaceId}/members/${memberId}`, { method: 'DELETE' })
}

/**
 * NORMALISED, not merely typed — and this is the boundary CLAUDE.md §4 means by "validate at every
 * boundary".
 *
 * A response type is a CLAIM about what the server sent, never a check of it, and the claim is
 * false for a whole ordinary class of deployment: the console and its Worker do not ship together
 * (CLAUDE.md §7), so a freshly built console can talk to a Worker older than itself. `workspaces`
 * was added to this row by `workspacesByMembership`; a Worker that predates it omits the key
 * entirely, the type still says `ReadonlyArray`, and `member.workspaces.slice(0, 2)` in AllMembers
 * threw "Cannot read properties of undefined (reading 'slice')" — which, with no error boundary in
 * the app, replaced the whole console with React Router's developer stack trace. The founder
 * photographed it.
 *
 * Guarding at each USE site would be a dozen guards that a thirteenth caller forgets. One
 * normalisation here means every component downstream can trust the type it is given, which is what
 * the type was always supposed to be worth.
 */
export async function listOrgMembers(): Promise<readonly OrgMemberResource[]> {
  const page = await api<{ readonly data: readonly Partial<OrgMemberResource>[] }>(
    '/v1/organization/members',
  )
  return (page.data ?? []).map((m) => ({
    id: m.id ?? '',
    user_id: m.user_id ?? '',
    role: m.role ?? 'member',
    status: m.status ?? 'active',
    display_name: m.display_name ?? null,
    email: m.email ?? null,
    // The one that crashed. An absent list is an empty list: this person is on no team we were told
    // about, which is exactly what the console should then draw.
    workspaces: Array.isArray(m.workspaces) ? m.workspaces : [],
    last_active_at: m.last_active_at ?? null,
    joined_at: m.joined_at ?? null,
  }))
}

export async function setMemberRole(memberId: string, role: string): Promise<void> {
  await api(`/v1/organization/members/${memberId}`, { method: 'PATCH', body: { role } })
}

export async function suspendMember(memberId: string): Promise<void> {
  await api(`/v1/organization/members/${memberId}`, { method: 'DELETE' })
}

/**
 * Normalised for the reason `listOrgMembers` is, and for a failure that is quieter than a crash.
 *
 * `workspace_role` and `last_sent_at` are typed `string | null`, and the screen guards them with
 * `=== null` — which is exactly right for null and misses `undefined` completely. On an older
 * Worker the Pending Invites tab therefore rendered the literal text " · undefined" beside an
 * address, and "sent Invalid Date" where the date belongs. Nothing throws; the screen simply lies
 * quietly, which is the harder kind to notice.
 */
export async function listInvitations(): Promise<readonly InvitationResource[]> {
  const page = await api<{ readonly data: readonly Partial<InvitationResource>[] }>(
    '/v1/organization/invitations',
  )
  return (page.data ?? []).map((i) => ({
    id: i.id ?? '',
    email: i.email ?? '',
    role: i.role ?? 'member',
    workspace_id: i.workspace_id ?? null,
    workspace_role: i.workspace_role ?? null,
    // Carried since 2026-09-16 (BUG-07). The route has always served them; this mapper dropped
    // them, so the Pending Invites tab could not name what an invitation actually grants and an
    // administrator had no way to tell two invitations apart.
    group_id: i.group_id ?? null,
    permission_profile_id: i.permission_profile_id ?? null,
    expires_at: i.expires_at ?? '',
    created_at: i.created_at ?? '',
    last_sent_at: i.last_sent_at ?? null,
    // `exactOptionalPropertyTypes` is on, so an absent optional stays ABSENT rather than becoming
    // an explicit undefined the type does not admit.
    ...(i.invitation_url === undefined ? {} : { invitation_url: i.invitation_url }),
  }))
}

/**
 * Invite one person or a batch.
 *
 * The response shape differs by size — one invitation answers with the object itself, carrying its
 * link; a batch answers with a list — because that is the shape the single-invitation route has
 * always had and `Invitations.tsx` reads it. Normalised here so no screen has to know.
 */
export async function createInvitations(input: {
  readonly emails: readonly string[]
  readonly role: string
  readonly workspace_id?: string | null
  readonly workspace_role?: string | null
  /** A CUSTOM group. Batch-wide, like the team — the route refuses a system one (ADR 0117). */
  readonly group_id?: string | null
  readonly permission_profile_id?: string | null
  readonly assignments?: ReadonlyArray<{
    readonly email: string
    readonly role?: string
    readonly workspace_role?: string
  }>
}): Promise<readonly InvitationResource[]> {
  const body: Record<string, unknown> = { role: input.role }
  if (input.assignments !== undefined) body['assignments'] = input.assignments
  else if (input.emails.length === 1) body['email'] = input.emails[0]
  else body['emails'] = input.emails
  if (input.workspace_id !== undefined && input.workspace_id !== null) {
    body['workspace_id'] = input.workspace_id
  }
  if (input.workspace_role !== undefined && input.workspace_role !== null) {
    body['workspace_role'] = input.workspace_role
  }
  // Omitted rather than sent as null when nothing was chosen: an absent key is what "the sender
  // did not pick one" means, and the route's unknown-field check reads the keys that are there.
  if (input.group_id !== undefined && input.group_id !== null) {
    body['group_id'] = input.group_id
  }
  if (input.permission_profile_id !== undefined && input.permission_profile_id !== null) {
    body['permission_profile_id'] = input.permission_profile_id
  }

  const response = await api<
    { readonly object?: string; readonly data?: readonly InvitationResource[] } & InvitationResource
  >('/v1/organization/invitations', { method: 'POST', body })
  if (response.object === 'list' && Array.isArray(response.data)) return response.data
  return [response]
}

export async function resendInvitation(id: string): Promise<InvitationResource> {
  return api<InvitationResource>(`/v1/organization/invitations/${id}/resend`, { method: 'POST' })
}

export async function revokeInvitation(id: string): Promise<void> {
  await api(`/v1/organization/invitations/${id}`, { method: 'DELETE' })
}

export async function listInviteRequests(): Promise<readonly InviteRequestResource[]> {
  const page = await api<{ readonly data: readonly InviteRequestResource[] }>(
    '/v1/organization/invite-requests',
  )
  return page.data
}

export async function decideInviteRequest(
  id: string,
  decision: 'approve' | 'deny',
  note?: string,
): Promise<void> {
  await api(`/v1/organization/invite-requests/${id}/${decision}`, {
    method: 'POST',
    body: note === undefined ? {} : { note },
  })
}

export type OnboardingState = {
  readonly flow_key: string
  readonly step_key: string
  readonly completed_at: string | null
  readonly dismissed_at: string | null
}

export async function listOnboarding(): Promise<readonly OnboardingState[]> {
  const page = await api<{ readonly data: readonly OnboardingState[] }>('/v1/profile/onboarding')
  return page.data
}

export async function setOnboarding(input: {
  readonly flow_key: string
  readonly step_key: string
  readonly completed?: boolean
  readonly dismissed?: boolean
}): Promise<void> {
  await api('/v1/profile/onboarding', { method: 'PATCH', body: input })
}

export async function setActiveWorkspace(workspaceId: string | null): Promise<void> {
  await api('/v1/profile/preferences', {
    method: 'PATCH',
    body: { active_workspace_id: workspaceId },
  })
}

// ---------------------------------------------------------------------------------------------
// Workspace Settings.

/**
 * The values, loosely typed on purpose.
 *
 * Thirty columns of three shapes, and the screen renders them from `SETTING_SPECS` rather than
 * from a hand-written field list — so a type that named each one would be a third copy of the same
 * table, drifting against the migration and the core module. What the screen needs from a value is
 * its shape, and the shape comes from the spec.
 */
export type WorkspaceSettingsResource = {
  readonly workspace_id: string
  readonly logo_storage_path: string | null
  readonly updated_at: string
} & Readonly<Record<string, unknown>>

/** Where each control is ENFORCED, so a stored-only one can say so instead of pretending. */
export type WorkspaceSettingsMeta = Readonly<
  Record<string, { readonly enforcement: string; readonly coming_soon: boolean }>
>

export type WorkspaceSettingsPage = {
  readonly data: WorkspaceSettingsResource
  readonly meta: WorkspaceSettingsMeta
}

export async function getWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettingsPage> {
  return api<WorkspaceSettingsPage>(`/v1/workspaces/${workspaceId}/settings`)
}

export async function patchWorkspaceSettings(
  workspaceId: string,
  patch: Readonly<Record<string, unknown>>,
): Promise<WorkspaceSettingsPage> {
  return api<WorkspaceSettingsPage>(`/v1/workspaces/${workspaceId}/settings`, {
    method: 'PATCH',
    body: patch,
  })
}

export async function resetWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettingsPage> {
  return api<WorkspaceSettingsPage>(`/v1/workspaces/${workspaceId}/settings/reset`, {
    method: 'POST',
  })
}
